import {
  useRef,
  useState,
  type CSSProperties,
  type PropsWithChildren,
} from 'react'
import Draggable, { type DraggableEvent } from 'react-draggable'
import { styled } from '@linaria/react'

import { useBoundStore } from '../../store/store'
import {
  Copyrights,
  CustomerServiceButton,
  FooterLinks,
  GoUp,
  InboxButton,
  Navbar,
  Sponsors,
  ThemeSwitch,
} from '../common'
import { LinariaClassName } from '@linaria/core'

const DragBoundary = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: -10;
  opacity: 0;
  pointer-events: none;
  width: 100vw;
  height: 100vh;
`

const FloatingButtons = styled.div`
  position: fixed;
  z-index: 15;
  bottom: 10px;
  right: 10px;
`

const Footer = styled.div`
  padding: 50px 0;
  position: relative;
  background-color: #002335;
`

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`

interface DesktopLayoutProps {
  hideScrollUp?: boolean
  hideChat?: boolean
  hideInbox?: boolean
  style?: CSSProperties
  hideBalance?: boolean
  className?: LinariaClassName
  hideThemeSwitch?: boolean
}

const DesktopLayout = ({
  children,
  style,
  hideScrollUp,
  hideInbox,
  hideChat,
  hideBalance,
  hideThemeSwitch,
  className,
}: PropsWithChildren<DesktopLayoutProps>) => {
  const setTheme = useBoundStore(state => state.common.setTheme)
  const theme = useBoundStore(state => state.common.theme)
  const nodeRef = useRef(null)
  const [position, setPosition] = useState({ clientX: 0, clientY: 0 })
  const [timeStamp, setTimeStamp] = useState(0)

  const onStop = (e: DraggableEvent) => {
    const clientX = (e as MouseEvent).clientX
    const clientY = (e as MouseEvent).clientY
    const dragTime = e.timeStamp - timeStamp
    if (
      (clientX === position.clientX && clientY === position.clientY) ||
      dragTime < 100
    ) {
      const { id } = e.target as HTMLDivElement
      if (id === 'float-message-button') {
        window.open('https://t.me/fun88mxchannel', '_blank')
      } else if (id === 'float-customer-service') {
        window.fcWidget?.open()
      } else if (id === 'go-up') {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      } else if (id === 'theme-switch') {
        if (theme === 'light') {
          setTheme('dark')
          localStorage.setItem('theme', 'dark')
          document.body.classList.remove('theme-light')
          !document.body.classList.contains('theme-dark') &&
            document.body.classList.add('theme-dark')
        } else {
          setTheme('light')
          localStorage.setItem('theme', 'light')
          document.body.classList.remove('theme-dark')
          !document.body.classList.contains('theme-light') &&
            document.body.classList.add('theme-light')
        }
      }
    }
  }

  const onStart = (e: DraggableEvent) => {
    const { clientX, clientY, timeStamp } = e as MouseEvent
    setPosition({ clientX: clientX, clientY: clientY })
    setTimeStamp(timeStamp)
  }

  return (
    <div className={className} style={style}>
      <DragBoundary id="drag-boundary" />
      <Navbar hideBalance={hideBalance} />
      <Draggable
        onStart={onStart}
        onStop={onStop}
        axis="y"
        bounds="#drag-boundary"
        nodeRef={nodeRef}>
        <FloatingButtons ref={nodeRef} className="v-flx-ctr">
          {!hideScrollUp && <GoUp />}
          {!hideInbox && <InboxButton />}
          {!hideChat && <CustomerServiceButton />}
          {!hideThemeSwitch && <ThemeSwitch theme={theme} />}
        </FloatingButtons>
      </Draggable>
      {children}
      <Footer>
        <FooterContent>
          <FooterLinks />
          <Sponsors />
          <Copyrights />
        </FooterContent>
      </Footer>
    </div>
  )
}

export default DesktopLayout
